// 介錯の幕2の頭: 咆哮 (執行の咆哮) の前に、部屋の真ん中へ移る (2026-10-10 に作り、2026-10-12 から既定 = 配布版。旧は DEV ?roarAir=0)。
//
//   ユーザー (10-10・窓口経由・原文): 「というか、この幕２にはいった時にボスが動画みたいに真ん中に移動すれば良さそう。
//     弾けたら介錯から距離が取れて最後の１発が弾きやすくなるでいいと思う」
//   読み = 幕2に入った時 (咆哮の前) に介錯が部屋の真ん中へ移る。真ん中から吠えれば、主人公のいる側にも場が残るので、
//     波を弾いて介錯から離れる向きへ飛べば必ず距離が取れ、最後の突進斬りを離れた所から見て弾ける。
//   文書 = docs/research/2026-10-10-executioner-roar-air-center.md
//
//   移り方 (今ある絵と動きだけ):
//     ・真ん中が介錯の後ろ (主人公と反対側) = 主人公を見たまま歩いて下がる。絵は立て直しの歩き (4コマの歩きを逆に回す・
//       歩幅は進んだ距離に合わせる = 足が滑らない)。速さも立て直しと同じ (歩き × 2 = 378px/秒)。
//     ・真ん中が介錯の前 (主人公の側) = いつもの前ステップ (沈み → 残像の踏み込み → 着地) を1歩。
//       距離だけ長い一歩 (速さはいつもの 1000px/秒)。主人公が間に立っていれば、その脇を踏み抜けて真ん中へ着き、向き直る。
//     ・主人公が真ん中のすぐ近く (136px 以内) に立っている時は、主人公を越えず・押しのけず、自分の側で 136px 空けて止まる
//       (今より主人公に近い所なら動かない)。主人公の後ろに広い場が残る。
//   着いたら 250ms 立ち止まって主人公へ向き直り (向き替えの1拍)、咆哮を始める。3 秒動いても着かなければその場で吠える。
//   致命の後の静止 (1.1 秒) はそのまま = 静止の間は動かない。移る間に殴れるのは今の幕の移りと同じ (待機中は刃で受ける = 傷は通らない)。
//
//   既定 = setRoarAir(true, 'away', true) が setRoarCenter(true) を呼ぶ (src/exec_roar_air.ts の読み込みの最後・2026-10-12 ユーザー採用 B-1)。
//   旧 (咆哮は間合いに入ってから) は DEV の ?roarAir=0 か &roarAirCenter=0。URL の読み取りは src/game.ts の DEV の箱だけ。
import { BOSS, bossBodyBoundsFor, FIXED_DT } from './balance'
import { advanceExecutionerStep, EXECUTIONER, type ExecutionerStepState } from './executioner_weapon'
import type { Boss, Player } from './types'

export const ROAR_CENTER_ATTACK_ID = 'executioner_phaseTransition'
const BOSS_ID = 'the-executioner'

export const ROAR_CENTER = {
  /** 主人公からこれより近い所へは立たない (刃の好みの間合い 136 = いつも攻撃の前に止まる距離)。 */
  gapPx: 136,
  /** 着いたとみなす (前ステップの不感帯と同じ)。 */
  deadbandPx: 16,
  /** 歩いて下がる速さ = 歩き × これ (立て直しの歩きと同じ 2)。 */
  walkBackMul: 2,
  /** 着いてから吠えるまで (向き替えの1拍 180ms を見せる)。 */
  holdMs: 250,
  /** これだけ動いても着かなければ、その場で吠える (主人公が追い回した時)。 */
  maxMoveMs: 3000,
} as const

let on = false
export function setRoarCenter(next: boolean): void { on = next }
export function roarCenterOn(): boolean { return on }

// 🔵 2026-10-23 検収 kensyu-exec-quake-size-1023 Q-4 のメモ (作者・原文):
//   「今の版・主人公が右寄り（前ステップで脇を抜ける）：どんな時もこれで真ん中に向かうようにしてほしい」
//   → 配布版は、真ん中が介錯の後ろ (主人公と反対側) にある時も、歩いて下がらず長い前ステップを1歩で真ん中へ向かう
//     (踏み込む向きを向いたまま・着いてから主人公へ向き直る)。不感帯 (16px) の内の残りだけは今までどおり歩いて詰める。
//   旧 (真ん中が後ろなら歩いて下がる) は DEV ?execAct2Path=walk。URL の読み取りは src/game.ts の DEV の箱だけ。
export type RoarCenterPath = 'step' | 'walk'
let path: RoarCenterPath = 'step'
export function setRoarCenterPath(next: RoarCenterPath): void { path = next }
export function setRoarCenterPathFromParams(raw: string | null): void { if (raw === 'walk') path = 'walk' }

// 11-04 作者「砲の直した版で、一瞬細い絵が挟まってるのを直してほしい…距離が遠い時一瞬だけ移動する…位置の微調整レベルしか動かない」
//   → 真ん中への長い前ステップは不感帯 (16px) の手前で着き、その残り (撮影では 10px) を歩きの絵で詰めるので、
//   細い横歩きの絵 (walk_cross) が 2 コマだけ出ていた。true = 不感帯の残りは歩きの絵を出さず立ち絵のまま詰める。
//   後ろへ歩いて下がる道筋 (?execAct2Path=walk) は今までどおり歩きの絵。DEV ?execCloak=cannon で true (配布版は false)。
let nudgeStill = false
export function setRoarCenterNudgeStill(next: boolean): void { nudgeStill = next }

/** 部屋の真ん中 (ボスが立てる範囲の真ん中)。介錯は 110〜850 なので 480。 */
export function roarCenterX(bossId: string = BOSS_ID): number {
  const b = bossBodyBoundsFor(bossId)
  return (b.leftWall + b.rightWall) / 2
}

/** 移る先。真ん中。主人公が真ん中の 136px 以内に立っていれば、介錯の側で主人公から 136px の所 (今より近い所なら動かない)。 */
export function roarCenterTargetX(bossX: number, playerX: number, bossId: string = BOSS_ID): number {
  const c = roarCenterX(bossId)
  if (Math.abs(c - playerX) >= ROAR_CENTER.gapPx) return c
  const side: 1 | -1 = playerX >= bossX ? 1 : -1     // 主人公は介錯から見てどちら側か
  const t = playerX - side * ROAR_CENTER.gapPx
  // t が今より主人公から遠い (後ろへ下がる) なら、下がらずその場に立つ。
  if ((t - bossX) * side < 0) return bossX
  const b = bossBodyBoundsFor(bossId)
  return Math.max(b.leftWall, Math.min(b.rightWall, t))
}

/** この tick に真ん中へ移る仕組みが働くか (介錯・幕2の頭・確定初手の咆哮がまだ)。 */
export function roarCenterApplies(boss: Boss, forcedRoomEntries: readonly { room: number; attackId: string }[] | undefined): boolean {
  if (!on || boss.id !== BOSS_ID || !boss.exec || boss.state !== 'idle') return false
  if (boss.exec.morph !== 'no') return false
  return !!forcedRoomEntries?.some((e) => e.attackId === ROAR_CENTER_ATTACK_ID
    && e.room === boss.executesTaken && !(boss.roomEntryForcedDone ?? []).includes(e.room))
}

/** 前ステップの沈み・踏み込み・着地の間は、踏み込む向きを向いたまま (主人公の脇を抜ける時に途中で振り返らない)。 */
export function roarCenterLocksFacing(boss: Boss): boolean {
  const step = boss.exec?.step
  return !!boss.roarCenter && boss.roarCenter.phase === 'move' && !!step && step.long === true
    && step.kind === 'forward' && step.phase !== 'settle'
}

export type RoarCenterTick = { boss: Boss; ready: boolean }

/**
 * 1 tick 進める。elapsedMs < 0 (致命の後の静止) の間は動かない。ready = 着いて向き直り終えた = 咆哮を始めてよい。
 * walkSpeed = 介錯の歩きの速さ (BOSS.walkSpeed × walkSpeedMul)。
 */
export function stepRoarCenter(boss: Boss, player: Player, elapsedMs: number, walkSpeed: number): RoarCenterTick {
  const tickMs = FIXED_DT * 1000
  const exec = boss.exec!
  const state = boss.roarCenter ?? { phase: 'move' as const, ms: 0 }
  if (elapsedMs < 0) return { boss: { ...boss, roarCenter: { ...state, walking: false } }, ready: false }
  const bounds = bossBodyBoundsFor(boss.id)

  if (state.phase === 'hold') {
    const ms = state.ms + tickMs
    return { boss: { ...boss, roarCenter: { phase: 'hold', ms } }, ready: ms >= ROAR_CENTER.holdMs }
  }

  const moveMs = state.ms + tickMs
  const target = roarCenterTargetX(boss.x, player.x, boss.id)
  const step = exec.step
  // 踏み込みの途中: 最後まで踏む (沈みの間は着地点を持ち直す)。
  if (step) {
    const adv = advanceExecutionerStep(step, boss.x, target - boss.x, tickMs, EXECUTIONER.step, 1e6, 'lunge')
    const x = Math.max(bounds.leftWall, Math.min(bounds.rightWall, adv.x))
    const facing = step.kind === 'forward' && step.phase !== 'settle' && adv.step && adv.step.phase !== 'settle'
      ? (step.direction > 0 ? 'right' as const : 'left' as const) : boss.facing
    return { boss: { ...boss, x, facing, exec: { ...exec, step: adv.step }, roarCenter: { phase: 'move', ms: moveMs } }, ready: false }
  }

  const dx = target - boss.x
  const towardPlayer: 1 | -1 = player.x >= boss.x ? 1 : -1
  const dir: 1 | -1 = dx > 0 ? 1 : -1
  // 歩きは真ん中ちょうどまで (1px 未満で止める)。前ステップは不感帯 (16px) の内なら踏まず、その残りも歩いて詰める
  //   (10-10: 届く範囲を 390 に縮めたので、真ん中から 16px ずれると片側の壁ぎわの安全な幅が 30→14px に減る)。
  //   16px 以下は歩き1サイクル (170px) の1割に満たない = 歩きの絵の向き (逆回し) は見分けられない。
  const walkBackPath = path === 'walk' && dir !== towardPlayer
  const walkBack = walkBackPath || Math.abs(dx) <= ROAR_CENTER.deadbandPx
  const arrived = Math.abs(dx) < 1
  if (arrived || moveMs >= ROAR_CENTER.maxMoveMs) {
    return { boss: { ...boss, roarCenter: { phase: 'hold', ms: 0 } }, ready: false }
  }
  if (walkBack) {
    // 真ん中が後ろ: 主人公を見たまま歩いて下がる (立て直しの歩きの絵・歩幅は進んだ距離に合わせる)。
    const stepPx = Math.min(Math.abs(dx), walkSpeed * ROAR_CENTER.walkBackMul * FIXED_DT)
    const x = Math.max(bounds.leftWall, Math.min(bounds.rightWall, boss.x + dir * stepPx))
    return { boss: { ...boss, x, roarCenter: { phase: 'move', ms: moveMs, walking: walkBackPath || !nudgeStill } }, ready: false }
  }
  // 真ん中が前: 長い前ステップを1歩 (沈みから。速さはいつもの一歩と同じ・歩幅の上限だけ外す)。
  const fresh: ExecutionerStepState = {
    kind: 'forward', style: 'lunge', phase: 'crouch', elapsedMs: 0, fromX: boss.x, toX: target,
    settleMs: EXECUTIONER.step.common.minSettleMs, direction: dir, commitOnLand: false, long: true,
  }
  const adv = advanceExecutionerStep(fresh, boss.x, dx, tickMs, EXECUTIONER.step, 1e6, 'lunge')
  return {
    boss: { ...boss, x: adv.x, facing: dir > 0 ? 'right' : 'left', exec: { ...exec, step: adv.step },
      roarCenter: { phase: 'move', ms: moveMs } },
    ready: false,
  }
}

/** 介錯の歩きの速さ (BOSS.walkSpeed × 介錯の walkSpeedMul 0.7)。boss.ts と同じ式。 */
export function roarCenterWalkSpeed(walkSpeedMul: number | undefined): number {
  return BOSS.walkSpeed * (walkSpeedMul ?? 1)
}
